import React, { useEffect, useState } from 'react';
import {
  TextField,
  InputAdornment,
  FormControl,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';

const TextInputField = ({
  label = '',
  value = '',
  fullWidth = true,
  disabled = false,
  placeholder = '',
  type = 'text',
  readOnly = false,
  variant = 'outlined',
  ref,
  startAdornment = null,
  endAdornment = null,
  onChange = () => {},
  className = '',
  helperText = '',
  error = false,
  inputProps = {},
  InputLabelProps = { shrink: true },
  radioOptions = [], // for radio buttons
  multiline = false, // Add multiline prop
  rows = 4, // Default number of rows for textarea
  maxRows = 8, // Maximum number of rows
}) => {
  const [editValue, setEditValue] = useState('');

  // Handle input change
  const handleChange = (event) => {
    setEditValue(event.target.value);
    onChange(event);
  };

  // Synchronize state with prop value
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Conditional rendering based on type
  if (type === 'radio') {
    return (
      <FormControl fullWidth>
        <FormHelperText>{label}</FormHelperText>
        <RadioGroup
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          row
          sx={{
            gap: {
              xs: 0, // Adjust gap for responsiveness
              sm: 0,
              md: 0,
              lg: 1,
              xl: 1,
            },
          }}
        >
          {radioOptions.map((option) => (
            <FormControlLabel
              disabled={disabled}
              key={option.value}
              value={option.value}
              control={
                <Radio
                  sx={{
                    color: 'blue', // Default color
                    '&.Mui-disabled': {
                      color: 'black', // Color for disabled Radio
                    },
                    '&.Mui-checked': {
                      color: 'blue', // Color when checked
                    },
                  }}
                />
              }
              label={option.label}
            />
          ))}
        </RadioGroup>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  // For textarea / multiline
  if (multiline || type === 'textarea') {
    return (
      <TextField
        ref={ref}
        label={label}
        value={editValue}
        fullWidth={fullWidth}
        disabled={disabled}
        placeholder={placeholder}
        variant={variant}
        onChange={handleChange}
        multiline={true}
        rows={rows}
        maxRows={maxRows}
        InputProps={{
          readOnly: readOnly,
          startAdornment: startAdornment && (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ),
          endAdornment: endAdornment && (
            <InputAdornment position="end">{endAdornment}</InputAdornment>
          ),
          ...inputProps,
        }}
        sx={{
          '& .MuiInputBase-input': {
            color: 'black', // Set text color for normal state
          },
          '& .MuiOutlinedInput-input.Mui-disabled': {
            color: 'black', // Set text color for disabled state
            WebkitTextFillColor: 'black', // Ensures text remains black in disabled state
          },
          '& .MuiOutlinedInput-root': {
            '& textarea': {
              overflowY: 'auto !important',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1'
              }
            }
          }
        }}
        helperText={helperText}
        error={error}
        className={className}
        InputLabelProps={InputLabelProps}
      />
    );
  }

  // Default TextField for all other types
  return (
    <TextField
      ref={ref}
      label={label}
      value={editValue}
      fullWidth={fullWidth}
      disabled={disabled}
      placeholder={placeholder}
      type={type}
      variant={variant}
      onChange={handleChange}
      InputProps={{
        readOnly: readOnly,
        startAdornment: startAdornment && (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ),
        endAdornment: endAdornment && (
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        ),
        ...inputProps,
      }}
      sx={{
        '& .MuiInputBase-input': {
          color: 'black', // Set text color for normal state
        },
        '& .MuiOutlinedInput-input.Mui-disabled': {
          color: 'black', // Set text color for disabled state
          WebkitTextFillColor: 'black', // Ensures text remains black in disabled state
        },
      }}
      helperText={helperText}
      error={error}
      className={className}
      InputLabelProps={InputLabelProps}
    />
  );
};

export default TextInputField;